// src/pages/YoungFounders.tsx
// Route: /young-founders — the long-form sales page for parents.
//
// Section order follows the long sales page blueprint used in CREATOROS
// (backend/seed/pageBlueprints.json, "sales_long"), which is built on Russell
// Brunson's sales letter: who it's for → the big promise → what it is →
// the problem → the story → the new way (and why the old ways failed, with
// the three false beliefs) → what's included → the price → the questions
// parents ask → one call to action → P.S.
//
// Honesty rules from that blueprint: no invented testimonials, statistics,
// "total value" figures or deadline (proof and deadline sections wait for
// real ones). The free first week, the 14-day money-back guarantee, the
// promise and the prices are the live ones (components/OfferPromise.tsx).
// Screens are real Future Smart Lab screens (demo family).

import { Link } from "react-router-dom";
import OfferPromise, { PRICE_NOTE } from "../components/OfferPromise";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Brain,
  Check,
  CircleHelp,
  Clock,
  Hammer,
  Lightbulb,
  MessageCircle,
  Mic2,
  Package,
  PlayCircle,
  Presentation,
  Rocket,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Seo from "../components/Seo";

const SIGN_UP = "/register";
const WHATSAPP = "https://wa.me/2347052499758";
const img = (name: string) => `/images/sales/${name}.jpg`;

/* ─── small building blocks ──────────────────────────────────── */

function Cta({ children = "Create my family account", sub = true }: { children?: React.ReactNode; sub?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <Link
        to={SIGN_UP}
        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gold-400 px-8 py-4 text-center text-base font-extrabold text-ink shadow-[0_14px_36px_rgba(201,162,39,0.35)] transition hover:-translate-y-0.5 hover:bg-gold-300 sm:text-lg"
      >
        {children} <ArrowRight className="h-5 w-5" />
      </Link>
      {sub && (
        <p className="text-center text-sm text-current opacity-75">
          Takes about five minutes. Then add your child and open their first week, free.
        </p>
      )}
    </div>
  );
}

function Shot({ src, alt, className = "", frame = false, contain = false }: { src: string; alt: string; className?: string; frame?: boolean; contain?: boolean }) {
  return (
    <figure className={`overflow-hidden rounded-[22px] bg-[#f5f4ef] shadow-[0_24px_60px_rgba(27,45,94,0.16)] ring-1 ring-[#e5e8ef] ${frame ? `flex aspect-[4/3.6] justify-center ${contain ? "items-center bg-white p-3" : "items-start"}` : ""} ${className}`}>
      <img src={src} alt={alt} loading="lazy" className={frame ? (contain ? "h-auto max-h-full w-full object-contain" : "h-full w-full object-cover object-top") : "block h-auto w-full"} />
    </figure>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="text-[13px] font-extrabold uppercase tracking-[0.16em] text-gold-500">{children}</p>;
}

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mt-3 text-[1.9rem] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[2.4rem]" style={{ textWrap: "balance" } as React.CSSProperties}>
    {children}
  </h2>
);

const P = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-[17px] leading-8 text-ink-soft ${className}`}>{children}</p>
);

/* ─── content ────────────────────────────────────────────────── */

const FOUR_SKILLS: { icon: LucideIcon; title: string; text: string }[] = [
  { icon: Lightbulb, title: "Find the idea", text: "They learn to notice problems around them, talk to the people who have them, and decide which one is worth solving." },
  { icon: Hammer, title: "Build it", text: "Websites, then apps, then full products with AI and hardware: the same tools professional developers use." },
  { icon: Wallet, title: "Raise the money", text: "Saving, pricing and profit first; later equity, cap tables and how investors decide. Then they pitch to a panel." },
  { icon: Users, title: "Lead the team", text: "Speaking, teamwork, integrity and discipline are taught and scored every week, alongside the code." },
];

const OLD_WAYS = [
  { name: "Coding classes", why: "A good start, but most stop at the code. Your child learns to make a calculator, not why anyone would pay for it." },
  { name: "Extra lessons for school", why: "Useful for exams. They don't teach a child to build something, sell it, or stand up and explain it." },
  { name: "YouTube tutorials", why: "Endless, and free. Nobody checks the work, so nobody tells your child what to fix next." },
  { name: "Waiting for university", why: "By then, the years when trying, failing and trying again cost the least have already passed." },
];

const BELIEFS = [
  {
    belief: "“My child isn't a maths genius.”",
    truth: "They don't need to be. At 6, lessons start with stories, games and building with their hands. Every lesson is one small step: get ready, watch, do, hand in.",
  },
  {
    belief: "“We don't live near a centre, and our week is already full.”",
    truth: "It's online, with no fixed class times. Lessons take about 30 to 40 minutes, done any time in the week, and the hardware kit comes to your door.",
  },
  {
    belief: "“Children can't really build businesses.”",
    truth: "By the last stage, teenagers build a product, test it with real users the family arranges, plan how it would be funded, and pitch it to a panel.",
  },
];

const STAGES = [
  { ages: "Ages 6–9", name: "Foundation Builders", img: "/images/landing/foundation.jpg", items: ["Build games and stories with Scratch", "Make their first web page", "Learn what money is and how to save", "Present to an audience with confidence"] },
  { ages: "Ages 9–12", name: "Explorers", img: "/images/landing/explorers.jpg", items: ["Build and publish a real website", "Write JavaScript apps that solve local problems", "Wire up an Arduino and sensors from their kit", "Pitch a solution to a panel"] },
  { ages: "Ages 13–18", name: "Innovators", img: "/images/landing/builder.jpg", items: ["Build full web and mobile apps", "Add real AI to products people use", "Learn equity, cap tables and venture funding", "Present a capstone product at the Grand Showcase"] },
];

const STACK: { icon: LucideIcon; title: string; text: string }[] = [
  { icon: PlayCircle, title: "675 short video lessons, from age 6 to 18", text: "One clear step at a time, with captions, a written version, and a data-saver version for mobile data." },
  { icon: BadgeCheck, title: "A tutor reviews every piece of work", text: "Usually within 48 hours: what went well, one thing to improve, and the next step, scored on six areas including character." },
  { icon: Package, title: "A hardware kit posted to your home every year", text: "For online and hybrid learners: Makey Makey and circuits for the youngest, an Arduino and sensors from age 9." },
  { icon: Presentation, title: "A showcase at the end of every term", text: "Your child records a pitch, a panel scores it, and you get written feedback and a certificate." },
  { icon: Sparkles, title: "A weekly email about your child's progress", text: "What they did, what the tutor said, and what's next. If they fall behind, you'll know early." },
  { icon: CircleHelp, title: "Help when they're stuck", text: "Every lesson has an “I'm stuck” button that walks them through solving it, and asks a tutor if they still can't." },
  { icon: ShieldCheck, title: "Built for children", text: "A simple child mode for ages 6–9, their own sign-in from 9, first names only, and no messages between children." },
];

const PRICES = [
  { ages: "Ages 6–9", name: "Foundation Builders", monthly: 18000 },
  { ages: "Ages 9–12", name: "Explorers", monthly: 22000 },
  { ages: "Ages 13–18", name: "Innovators", monthly: 28000 },
];
const naira = (n: number) => `₦${n.toLocaleString("en-NG")}`;

const FAQ = [
  { q: "My child has never written any code. Is that a problem?", a: "No. Every age group starts from the beginning, and each lesson builds on the one before." },
  { q: "How much time does it take each week?", a: "Each lesson takes about 30 to 40 minutes, and a week has 4 to 8 of them. Your child chooses when to do them, as long as the week's goal is met by the end of the week." },
  { q: "Are there live classes?", a: "No. Lessons are short videos your child watches when it suits your family. A tutor reviews everything they hand in and writes back. On-site and hybrid learning are opening area by area." },
  { q: "What device do we need?", a: "A laptop or desktop computer for most lessons, with an internet connection. The youngest children do much of their work away from the screen, with things from home and from their kit." },
  { q: "What do I have to do as a parent?", a: "For ages 6–9, sit with your child to hand in their work; each week comes with a short note for you. From 9, children can sign in on their own and you follow along from your dashboard." },
  { q: "Will my child actually raise money for a business?", a: "We teach how founders raise money, and by the last stage your child plans how their product would be funded and pitches it to a panel. We don't ask anyone under 18 to apply for investment, and we don't promise funding. They leave knowing how it's done." },
  { q: "Is it safe?", a: "Children's work is private to your family and their tutor. Public projects show a first name only, accounts on outside sites are set up with a parent, and children never message each other or strangers." },
  { q: "What about data costs?", a: "Every video has a data-saver version, and every lesson can be read instead of watched." },
  { q: "Can I pay monthly?", a: "Yes. You can also pay for a quarter and save 10%, or for the year and save 20%. Two children: 10% off the second. Three or more: 15% off every child but the eldest." },
  { q: "Can we try it before paying?", a: "Yes. Your child's first week is free: every lesson of Week 1, with tutor feedback, for 7 days. No payment, no card." },
  { q: "What if it isn't right for us?", a: "Ask for a full refund within 14 days of your first payment, straight from your Payments page." },
];

/* ═══ PAGE ═══════════════════════════════════════════════════════ */

export default function YoungFounders() {
  return (
    <div className="min-h-screen bg-paper font-sans text-ink antialiased">
      <Seo
        title="Raise a young founder"
        description="An online academy for Nigerian children aged 6–18: by 18 they can find an idea, build it into real technology, plan how to fund it, and lead the team behind it."
        path="/young-founders"
      />

      {/* Slim header: the logo and the one action */}
      <header className="sticky top-0 z-30 border-b border-[#e5e8ef] bg-paper/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <span className="flex items-center gap-2.5">
            <img src="/images/logo.png" alt="" className="h-9 w-9 rounded-xl" />
            <span className="text-[15px] font-extrabold tracking-[-0.02em]">Future Smart Lab</span>
          </span>
          <Link to={SIGN_UP} className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-brand-700">
            Get started
          </Link>
        </div>
      </header>

      {/* 1–3. Who it's for, the big promise, what it is */}
      <section className="relative overflow-hidden bg-brand-600 text-white">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold-400/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p className="inline-flex rounded-full border border-gold-300/50 bg-white/5 px-4 py-1.5 text-[13px] font-bold text-gold-300">
              For parents of children aged 6 to 18 who want more than another coding class
            </p>
            <h1 className="mt-6 text-[2.2rem] font-extrabold leading-[1.08] tracking-[-0.03em] sm:text-[3.3rem]" style={{ textWrap: "balance" } as React.CSSProperties}>
              By 18, your child can find an idea, build it into real technology, raise money for it, and lead the team behind it.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/80">
              Future Smart Lab is an online academy that takes children from their first game at 6 to their own
              product and investor-style pitch at 18. Short video lessons, real projects at home, and a tutor's
              feedback on every piece of work.
            </p>
            <div className="mt-8 flex flex-col items-start gap-3">
              <Cta sub={false}>Start my child's journey</Cta>
              <p className="text-sm text-white/65">Online, anywhere in Nigeria. From {naira(18000)} a month.</p>
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-sm lg:max-w-none">
            <img src="/images/landing/hero.jpg" alt="A smiling learner holding her laptop" className="w-full rounded-[28px] object-cover shadow-[0_30px_80px_rgba(0,0,0,0.35)]" />
            <img
              src={img("phone-lesson")}
              alt="A lesson on a phone: the activity, one step at a time"
              className="absolute -bottom-6 left-4 hidden w-36 rounded-[22px] border-4 border-white shadow-[0_20px_50px_rgba(0,0,0,0.35)] sm:block lg:w-40"
            />
          </div>
        </div>
      </section>

      <main>
        {/* 4. The problem, in the parent's words */}
        <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <Eyebrow>If this sounds like your house</Eyebrow>
          <H2>Your child can use almost any app. But who is teaching them to build one?</H2>
          <div className="mt-6 space-y-5">
            <P>
              You can see where the world is going. The phones, the apps, the businesses that start with one laptop and an
              idea. And you can see your child, bright and curious, spending hours on things other people built.
            </P>
            <P>School is teaching them to pass exams. Maybe you've tried a coding class: they made a calculator, then it stopped. Nobody showed them what the code was <em>for</em>.</P>
            <P>
              And there's the quiet worry underneath it all: the jobs you know may not be the jobs that are there when
              they grow up. You don't want your child waiting until university, or their first job, to find out what
              they're capable of.
            </P>
          </div>
        </section>

        {/* 5. The story: why the academy exists */}
        <section className="bg-white">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2">
            <img src="/images/landing/building.jpg" alt="Two girls building a robot together" className="w-full rounded-[28px] object-cover lg:max-h-[620px]" loading="lazy" />
            <div>
              <Eyebrow>Why we built Future Smart Lab</Eyebrow>
              <H2>The founders behind Paystack, Flutterwave and Andela were children once.</H2>
              <div className="mt-6 space-y-5">
                <P>
                  What they had in common wasn't a certificate in coding. They could see a problem worth solving. They
                  could build something that solved it. They could convince people to back them. And they could lead the
                  people who built it with them.
                </P>
                <P>
                  Those four things are almost never taught together, and almost never to children. So we built a
                  programme that teaches all four, every week, from age 6, in the setting your child already lives in.
                </P>
                <P className="font-semibold text-ink">The next great African founder might be sitting in your house right now. Our job is to make sure they're ready.</P>
              </div>
            </div>
          </div>
        </section>

        {/* 6. The new way, and why the old ways didn't work */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Eyebrow>Not a coding class. A founder's path.</Eyebrow>
            <H2>Four skills, built together, every single week</H2>
            <P className="mt-4">
              Every week has lessons in technology, in money and business, and in leadership, and every term ends with a
              showcase. By 18, the four come together in one product of their own.
            </P>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FOUR_SKILLS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="rounded-[24px] bg-white p-6 ring-1 ring-[#e5e8ef]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <p className="mt-4 text-[13px] font-bold text-gold-500">Skill {i + 1}</p>
                <p className="text-lg font-extrabold text-ink">{title}</p>
                <p className="mt-2 text-[15px] leading-7 text-ink-mute">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="text-xl font-extrabold text-ink">Why the usual options fall short</h3>
              <ul className="mt-5 space-y-4">
                {OLD_WAYS.map((o) => (
                  <li key={o.name} className="flex gap-3">
                    <X className="mt-1 h-5 w-5 flex-shrink-0 text-[#b94a3a]" />
                    <span className="text-[16px] leading-7 text-ink-soft">
                      <b className="text-ink">{o.name}.</b> {o.why}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-ink">Three things parents tell us, and the truth</h3>
              <div className="mt-5 space-y-4">
                {BELIEFS.map((b) => (
                  <div key={b.belief} className="rounded-[20px] bg-white p-5 ring-1 ring-[#e5e8ef]">
                    <p className="text-[16px] font-bold text-ink">{b.belief}</p>
                    <p className="mt-2 flex gap-2.5 text-[15px] leading-7 text-ink-soft">
                      <Check className="mt-1 h-5 w-5 flex-shrink-0 text-[#2a7a56]" /> {b.truth}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How a lesson works: real screens */}
        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <Eyebrow>How it works</Eyebrow>
              <H2>One small step at a time, with a tutor beside every one</H2>
              <P className="mt-4">This is the real lesson screen your child uses. Each lesson has four steps, and only one thing to do on each.</P>
            </div>
            <div className="mt-12 grid gap-8 lg:grid-cols-3">
              {[
                { n: 1, title: "Get ready, then watch", text: "Your child sees what they'll learn and what to have ready, then watches a short video lesson.", src: img("lesson-watch"), alt: "The Watch step of a lesson, with the video playing" },
                { n: 2, title: "Do the activity", text: "They build, test or practise, ticking off each step. Much of it happens away from the screen.", src: img("lesson-do"), alt: "The Do step of a lesson: a checklist of activity steps" },
                { n: 3, title: "Hand in, get feedback", text: "They upload what they made. A tutor replies with what went well, one thing to improve, and the next step.", contain: true, src: img("feedback"), alt: "A tutor's feedback: what went well, one thing to improve, the next step" },
              ].map((s) => (
                <div key={s.n}>
                  <Shot src={s.src} alt={s.alt} frame contain={"contain" in s && s.contain} />
                  <p className="mt-5 flex items-center gap-2 text-lg font-extrabold text-ink">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold-400 text-sm text-ink">{s.n}</span>
                    {s.title}
                  </p>
                  <p className="mt-1.5 text-[15px] leading-7 text-ink-mute">{s.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
              <Shot src={img("parent-dashboard")} alt="The parent dashboard: each child's week, feedback to read and whether they're on track" />
              <div>
                <Eyebrow>For you</Eyebrow>
                <h3 className="mt-3 text-[1.6rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">You always know where your child is</h3>
                <P className="mt-4">
                  Your dashboard shows each child's week, the feedback waiting to be read, and whether they're on track.
                  Every week you get an email with what they did and what the tutor said. If they fall two weeks behind,
                  you hear about it straight away, not at the end of term.
                </P>
              </div>
            </div>

            <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
              <div className="lg:order-2">
                <Shot src={img("child-mode")} alt="Child mode for ages 6 to 9: big, simple and locked to their lessons" />
              </div>
              <div>
                <Eyebrow>For the youngest</Eyebrow>
                <h3 className="mt-3 text-[1.6rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">A simple screen made for small hands</h3>
                <P className="mt-4">
                  Hand your phone or laptop to your 6-year-old in child mode: bigger text, only their lessons, and a
                  grown-up question to leave. From 9, children get their own sign-in, and you still see everything.
                </P>
              </div>
            </div>

            <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
              <Shot src={img("showcase-panel")} alt="The showcase panel scoring a child's recorded pitch" />
              <div>
                <Eyebrow>Every term</Eyebrow>
                <h3 className="mt-3 text-[1.6rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">A showcase that builds real confidence</h3>
                <P className="mt-4">
                  At the end of every term, your child presents what they built and pitches it, on video, to your family.
                  A panel scores it on the things that matter, writes back, and your child receives a certificate. Term
                  after term, standing up and explaining their work becomes normal.
                </P>
              </div>
            </div>
          </div>
        </section>

        {/* The journey to 18 */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Eyebrow>From 6 to 18</Eyebrow>
            <H2>What your child will be able to do</H2>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {STAGES.map((s) => (
              <div key={s.name} className="overflow-hidden rounded-[24px] bg-white ring-1 ring-[#e5e8ef]">
                <img src={s.img} alt="" className="h-48 w-full object-cover" loading="lazy" />
                <div className="p-6">
                  <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-gold-500">{s.ages}</p>
                  <p className="text-xl font-extrabold text-ink">{s.name}</p>
                  <ul className="mt-4 space-y-2.5">
                    {s.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-[15px] leading-6 text-ink-soft">
                        <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2a7a56]" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-4 rounded-[24px] bg-brand-600 p-8 text-center text-white sm:flex-row sm:text-left">
            <Rocket className="h-10 w-10 flex-shrink-0 text-gold-300" />
            <p className="text-lg leading-8">
              <b className="text-gold-300">At 18:</b> a product they built, tested with real users, a plan for how it would
              be funded, and a pitch they've delivered to a panel. Not a certificate in coding: the start of a company.
            </p>
          </div>
        </section>

        {/* 7–8. Everything included, and the price */}
        <section className="bg-white">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
            <div className="text-center">
              <Eyebrow>Everything your child gets</Eyebrow>
              <H2>Here's what's included when you enrol</H2>
            </div>
            <ul className="mt-10 divide-y divide-[#e5e8ef] overflow-hidden rounded-[26px] ring-1 ring-[#e5e8ef]">
              {STACK.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex gap-4 bg-white p-5 sm:p-6">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gold-100 text-gold-500">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span>
                    <span className="block text-[17px] font-extrabold text-ink">{title}</span>
                    <span className="mt-1 block text-[15px] leading-7 text-ink-mute">{text}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-14 rounded-[28px] bg-brand-600 p-6 text-white sm:p-10">
              <p className="text-center text-[13px] font-extrabold uppercase tracking-[0.16em] text-gold-300">Your investment</p>
              <p className="mx-auto mt-3 max-w-xl text-center text-[1.6rem] font-extrabold leading-tight sm:text-[2rem]">
                Online learning, per child, per month
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {PRICES.map((p) => (
                  <div key={p.name} className="rounded-[22px] bg-white/10 p-5 text-center ring-1 ring-white/15">
                    <p className="text-[13px] font-bold text-gold-300">{p.ages}</p>
                    <p className="text-sm text-white/75">{p.name}</p>
                    <p className="mt-3 text-[2rem] font-extrabold tabular-nums">{naira(p.monthly)}</p>
                    <p className="text-sm text-white/65">a month</p>
                  </div>
                ))}
              </div>
              <p className="mx-auto mt-6 max-w-2xl text-center text-[15px] text-white/80">
                {PRICE_NOTE} Everything above is included.
              </p>
              <div className="mt-8">
                <OfferPromise dark />
              </div>
              <div className="mt-8 text-white">
                <Cta>Start my child's free week</Cta>
              </div>
            </div>
          </div>
        </section>

        {/* Questions parents ask */}
        <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="text-center">
            <Eyebrow>Questions parents ask</Eyebrow>
            <H2>Before you decide</H2>
          </div>
          <div className="mt-10 divide-y divide-[#e5e8ef] overflow-hidden rounded-[26px] bg-white ring-1 ring-[#e5e8ef]">
            {FAQ.map((f) => (
              <details key={f.q} className="group p-5 sm:p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-[16px] font-bold text-ink">
                  {f.q}
                  <span className="mt-0.5 text-2xl leading-none text-gold-500 transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[15px] leading-7 text-ink-mute">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 flex items-center justify-center gap-2 text-center text-[15px] text-ink-mute">
            <MessageCircle className="h-5 w-5 text-gold-500" />
            Another question?
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="font-bold text-brand-600 underline-offset-4 hover:underline">
              Ask us on WhatsApp
            </a>
          </p>
        </section>

        {/* Final call to action + P.S. */}
        <section className="relative overflow-hidden bg-brand-600 text-white">
          <div aria-hidden className="pointer-events-none absolute -left-24 -bottom-24 h-96 w-96 rounded-full bg-gold-400/15 blur-3xl" />
          <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
            <div className="flex justify-center gap-3 text-gold-300">
              <Lightbulb className="h-7 w-7" /> <Hammer className="h-7 w-7" /> <Wallet className="h-7 w-7" /> <Mic2 className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-[2rem] font-extrabold leading-tight tracking-[-0.02em] sm:text-[2.6rem]" style={{ textWrap: "balance" } as React.CSSProperties}>
              Your child's first lesson can be this week
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg leading-8 text-white/80">
              Create your family account, add your child, and choose how they'll learn and how you'll pay. Their first week
              starts as soon as they're enrolled.
            </p>
            <div className="mt-8">
              <Cta>Create my family account</Cta>
            </div>
            <div className="mx-auto mt-14 max-w-2xl rounded-[22px] bg-white/5 p-6 text-left ring-1 ring-white/10">
              <p className="text-[16px] leading-8 text-white/85">
                <b className="text-gold-300">P.S.</b> The skills that build businesses are the same at 8 as at 28: notice a
                problem, build something that solves it, convince people to back it, and lead the people who build it with
                you. Future Smart Lab teaches all four, every week, from age 6, so that by 18 your child isn't waiting for
                permission to start.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-ink px-4 py-8 text-center text-[13px] text-white/50">
        <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Future Smart Lab · Abuja, Nigeria</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> Online, self-paced</span>
          <span className="inline-flex items-center gap-1.5"><Brain className="h-4 w-4" /> Technology · AI · Business · Leadership</span>
        </p>
        <p className="mt-3">
          <Link to="/" className="hover:text-white">futuresmartlab.com</Link> · <Link to="/contact" className="hover:text-white">Contact</Link>
        </p>
      </footer>
    </div>
  );
}
